import React, { useState, useEffect, useMemo } from 'react';
import { promptService, domainService } from '../../services';
import { Prompt, Domain } from '../../types';
import { PromptFormModal } from '../../components/admin/PromptFormModal';
import { PromptViewerModal } from '../../components/admin/PromptViewerModal';
import { FilterableTable } from '../../components/ui';
import type { FilterableColumnDef } from '../../components/ui';
import { PageContainer } from '../../components/layout';
import { UsagesModal } from '../../components/catalog/UsagesModal';
import { useCatalogUsages } from '../../components/catalog/useCatalogUsages';

export const PromptsPage: React.FC = () => {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [domains, setDomains] = useState<Domain[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showViewer, setShowViewer] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [duplicateMode, setDuplicateMode] = useState(false);  // PROD1.7.7.4

  const usages = useCatalogUsages('prompts');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [promptsData, domainsData] = await Promise.all([
        promptService.list({ include_deprecated: true }),
        domainService.list()
      ]);
      setPrompts(promptsData);
      setDomains(domainsData);
    } catch (error) {
      console.error('Error loading prompts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (prompt: Prompt) => {
    if (!confirm(`Supprimer le prompt "${prompt.name}" ?`)) return;
    await usages.handleDeleteWithGuard(
      prompt.id,
      prompt.name,
      () => promptService.delete(prompt.id),
      () => loadData()
    );
  };

  const handleDeprecate = async (prompt: Prompt) => {
    try {
      await promptService.update(prompt.id, { is_deprecated: !prompt.is_deprecated });
      loadData();
    } catch (error) {
      console.error('Error updating prompt:', error);
    }
  };

  const columns: FilterableColumnDef<Prompt>[] = useMemo(() => [
    {
      key: 'name',
      header: 'Nom',
      sortable: true,
      searchable: true,
      render: (p) => p.name,
    },
    {
      key: 'version',
      header: 'Version',
      sortable: true,
      render: (p) => p.version,
    },
    {
      key: 'type',
      header: 'Type',
      filterable: true,
      render: (p) => (
        <span className="px-2 py-1 bg-gray-200 rounded text-sm">{p.type}</span>
      ),
    },
    {
      key: 'domain',
      header: 'Domaine',
      filterable: true,
      searchable: true,
      render: (p) => p.domain,
    },
    {
      key: 'status',
      header: 'Statut',
      filterable: true,
      filterValueFn: (p) => p.is_deprecated ? ['deprecated'] : ['actif'],
      filterOptions: [
        { value: 'actif', label: 'Actif' },
        { value: 'deprecated', label: 'Deprecated' },
      ],
      render: (p) => (
        <div className="flex gap-1">
          {p.is_deprecated && (
            <span className="px-2 py-0.5 bg-orange-100 text-orange-800 rounded text-xs">Deprecated</span>
          )}
        </div>
      ),
    },
    {
      key: 'usages',
      header: 'Activé dans',
      sortable: true,
      sortValueFn: (p) => String(p.tenants_count ?? 0).padStart(6, '0'),
      render: (p) => (
        <button
          onClick={() => usages.openModal(p.id, p.name)}
          className="text-blue-600 hover:underline text-sm"
          title="Voir les tenants où ce prompt est activé"
        >
          {p.tenants_count ?? 0} tenants
        </button>
      ),
    },
    {
      key: 'runs',
      header: 'Runs',
      sortable: true,
      sortValueFn: (p) => String(p.used_in_runs_count).padStart(6, '0'),
      render: (p) => (
        <span className="text-gray-600 text-sm">{p.used_in_runs_count} runs</span>
      ),
    },
    {
      key: 'created_by',
      header: 'Créé par',
      filterable: true,
      filterValueFn: (p) => p.created_by_name ? [p.created_by_name] : [],
      render: (p) => <span className="text-sm text-gray-500">{p.created_by_name || '-'}</span>,
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (p) => (
        <div className="flex gap-2">
          <button onClick={() => { setSelectedPrompt(p); setShowViewer(true); }} className="text-blue-600 hover:underline text-sm">
            Voir
          </button>
          <button onClick={() => { setSelectedPrompt(p); setEditMode(true); setDuplicateMode(false); setShowForm(true); }} className="text-blue-600 hover:underline text-sm">
            Modifier
          </button>
          <button onClick={() => { setSelectedPrompt(p); setEditMode(false); setDuplicateMode(true); setShowForm(true); }} className="text-teal-600 hover:underline text-sm">
            Dupliquer
          </button>
          <button onClick={() => handleDeprecate(p)} className="text-orange-600 hover:underline text-sm">
            {p.is_deprecated ? 'Restaurer' : 'Deprecate'}
          </button>
          <button
            onClick={() => handleDelete(p)}
            className="text-sm text-red-600 hover:underline"
            title="Supprimer"
          >
            Supprimer
          </button>
        </div>
      ),
    },
  ], [usages]);

  return (
    <PageContainer>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Gestion des Prompts</h1>
        <button
          onClick={() => { setEditMode(false); setDuplicateMode(false); setSelectedPrompt(null); setShowForm(true); }}
          className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-600"
        >
          + Nouveau Prompt
        </button>
      </div>

      {loading ? (
        <div>Chargement...</div>
      ) : (
        <FilterableTable
          persistKey="parametres-prompts"
          paginated
          data={prompts}
          columns={columns}
          rowKey={(p) => p.id}
          defaultSort={{ key: 'name', direction: 'asc' }}
          rowClassName={(p) => p.is_deprecated ? 'text-gray-400' : ''}
          noDataMessage="Aucun prompt trouvé"
          emptyMessage="Aucun prompt ne correspond aux filtres"
        />
      )}

      {showForm && (
        <PromptFormModal
          prompt={editMode ? selectedPrompt : null}
          duplicateFrom={duplicateMode ? selectedPrompt : null}
          domains={domains}
          onClose={() => { setShowForm(false); setDuplicateMode(false); }}
          onSave={() => { setShowForm(false); setDuplicateMode(false); loadData(); }}
        />
      )}

      {showViewer && selectedPrompt && (
        <PromptViewerModal
          prompt={selectedPrompt}
          onClose={() => setShowViewer(false)}
        />
      )}

      <UsagesModal
        open={usages.modal.open}
        catalogType="prompts"
        objectId={usages.modal.objectId}
        objectLabel={usages.modal.objectLabel}
        onClose={usages.closeModal}
      />
    </PageContainer>
  );
};
